<template>
  <div class="postersPage-container">
    <!-- 搜尋列 -->
    <div class="search-bar">
      <input
        v-model="searchInput"
        type="text"
        placeholder="搜尋保養知識..."
        class="search-input"
        @keyup.enter="doSearch"
      />
      <button class="search-btn" @click="doSearch">搜尋</button>
    </div>

    <h1 v-if="!poster && loadingH1">{{ loadingH1 }}</h1>
    <div v-if="!poster" class="loading">載入中...</div>
    <template v-if="poster">
    <!-- 麵包屑導航 -->
    <nav class="breadcrumb" aria-label="breadcrumb">
      <router-link to="/">JiLi 首頁</router-link>
      <span class="breadcrumb-sep">›</span>
      <router-link to="/posters">保養知識</router-link>
      <span class="breadcrumb-sep">›</span>
      <span class="breadcrumb-current">{{ poster.title }}</span>
    </nav>

    <h1>{{ poster.title }}</h1>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <!-- 如果後台 layoutType === 'image-left' 或 'image-right' 就用「雜誌式浮動」 -->
    <div v-if="layoutClassFloat" class="float-container">
      <!-- 浮動圖片 -->
      <img
        v-if="poster.coverImage"
        :src="poster.coverImage"
        :alt="poster.title || 'JiLi 保養知識'"
        :class="[imageFloatClass, imageAnimationClass]"
      />
      <!-- 文字：自動繞行圖片直到圖片底部 -->
      <div
        class="float-text-content"
        :class="textAnimationClass"
        v-html="contentHtml"
      ></div>
    </div>

    <!-- 否則仍用 flex 或全幅 (你原本保留) -->
    <div v-else :class="layoutClassFlex">
      <div class="image-wrapper" :class="imageAnimationClass">
        <img :src="poster.coverImage" :alt="poster.title || 'JiLi 保養知識'" />
      </div>
      <div class="text-wrapper" :class="textAnimationClass">
        <div v-html="contentHtml"></div>
      </div>
    </div>

    <!-- 返回文章列表 -->
    <div class="back-to-list">
      <router-link :to="{ path: '/posters', query: fromPage ? { page: fromPage } : {} }" class="btn-back">
        &larr; 返回文章列表
      </router-link>
    </div>
    </template><!-- end v-if="poster" -->
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'

const route = useRoute()
const router = useRouter()

// 搜尋
const searchInput = ref('')
function doSearch() {
  const kw = searchInput.value.trim()
  if (!kw) {
    router.push('/posters')
  } else {
    router.push({ path: '/posters', query: { search: kw } })
  }
}
const poster = ref(null)

// 首次進站、資料還在載入時，先用伺服器注入的 og:title 當 h1（搜尋引擎可能在載入中就拍照）
function serverPageTitle() {
  try {
    const url = (document.querySelector('meta[property="og:url"]') || {}).content || ''
    if (url.replace(/\/$/, '') !== (location.origin + location.pathname).replace(/\/$/, '')) return ''
    const t = (document.querySelector('meta[property="og:title"]') || {}).content || ''
    return t.replace(/｜JiLi 肌黎$/, '')
  } catch (e) { return '' }
}
const loadingH1 = serverPageTitle()


// 文章中段「清潔 → 保濕 → 加強」三個產品的文字連結清單，顯示時換成有圖片的產品卡片
// （只改顯示，不動資料庫裡的文章內容；289 篇自動套用）
const PRODUCT_CARDS = {
  100009: { step: '清潔', name: '璀璨之秘潔顏慕斯', tag: '胺基酸洗面乳・泡沫細緻・洗後不緊繃', img: 'https://img.jiliskin.com/20260308_001.webp' },
  100011: { step: '保濕', name: '璀璨之秘外泌體安瓶', tag: '保濕修護安瓶・清爽不黏膩・醫美術後保濕適用', img: 'https://img.jiliskin.com/20260308_007.webp' },
  100001: { step: '加強', name: '璀璨之秘外泌體面膜', tag: '保濕修護面膜・每片 25ml 精華液・醫美術後保養適用', img: 'https://img.jiliskin.com/20260308_008.webp' }
}
function productCardsHtml(ids) {
  return '<div class="jl-cards">' + ids.map(id => {
    const p = PRODUCT_CARDS[id]
    return '<a class="jl-card" href="/product/' + id + '">' +
      '<img src="' + p.img + '" alt="' + p.name + '" loading="lazy">' +
      '<div><span class="jl-step">' + p.step + '</span>' +
      '<div class="jl-name">' + p.name + '</div>' +
      '<div class="jl-tag">' + p.tag + '</div>' +
      '<span class="jl-btn">看看產品 →</span></div></a>'
  }).join('') + '</div>'
}
const contentHtml = computed(() => {
  const html = (poster.value && poster.value.content) || ''
  // 找出第一個「含 2 個以上產品連結」的 <ul>，依原本順序換成卡片（每篇只換一組，避免重複推銷）
  let done = false
  return html.replace(/<ul>(?:(?!<\/ul>)[\s\S])*?<\/ul>/g, (ul) => {
    if (done) return ul
    const ids = [...ul.matchAll(/product\/(100009|100011|100001)/g)].map(m => m[1])
    const uniq = [...new Set(ids)].filter(id => PRODUCT_CARDS[id])
    if (uniq.length < 2) return ul
    done = true
    return productCardsHtml(uniq)
  })
})
const fromPage = route.query.fromPage || null
const errorMsg = ref('')

// 1) 撈取 API，並在插入 DOM 後掛 IntersectionObserver
onMounted(async () => {
  try {
    const id = route.params.id

    fetchPosterData(id)

  } catch (err) {
    console.error(err)
    errorMsg.value = '載入 poster 資料失敗'
  }
})

const fetchPosterData = async (id) => {
  try {
    if (!id) {
      errorMsg.value = '缺少 id 參數'
      return
    }

    const response = await axios.get(`/api/load/posterD/${id}`)
    poster.value = response.data
    updateSeoDescription();

    await nextTick()
    const textEls = document.querySelectorAll('.text-animation')
    const imgEls = document.querySelectorAll('.image-animation')
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible')
        }
      })
    })
    textEls.forEach(el => observer.observe(el))
    imgEls.forEach(el => observer.observe(el))
  } catch (err) {
    console.error(err)
    errorMsg.value = '載入 poster 資料失敗'
  }
}

async function updateSeoDescription() {
  const docTitle = (poster.value.title || '') + ' | JiLi 保養知識'
  const docDesc = poster.value.seo_description
    || poster.value.title
    || 'JiLi 保養知識 — 美容保養專業文章'

  const pageUrl = `https://jiliskin.com/posters/${route.params.id}`

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: poster.value.title || '',
    description: docDesc,
    // 作者/發行者接到首頁同一份品牌資料（index.html 的 #organization），文章曝光才會算到同一個品牌
    author: {
      '@type': 'Organization',
      '@id': 'https://jiliskin.com/#organization',
      name: 'JiLi 肌黎',
      url: 'https://jiliskin.com/'
    },
    publisher: {
      '@type': 'Organization',
      '@id': 'https://jiliskin.com/#organization',
      name: 'JiLi 肌黎',
      logo: {
        '@type': 'ImageObject',
        url: 'https://img.jiliskin.com/jili_logo_600.png'
      }
    },
    ...(poster.value.datePublished && { datePublished: poster.value.datePublished }),
    ...(poster.value.dateModified && { dateModified: poster.value.dateModified }),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl
    },
    url: pageUrl
  }
  if (poster.value.coverImage) {
    schema.image = [poster.value.coverImage]
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'JiLi 首頁',
        item: 'https://jiliskin.com/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '保養知識',
        item: 'https://jiliskin.com/posters'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: poster.value.title || '',
        item: pageUrl
      }
    ]
  }

  useHead({
    title: docTitle,
    meta: [
      { name: 'description', content: docDesc },
      { property: 'og:title', content: docTitle },
      { property: 'og:description', content: docDesc },
      { property: 'og:image', content: poster.value.coverImage || '' },
      { property: 'og:type', content: 'article' },
    ],
    script: [
      { type: 'application/ld+json', children: JSON.stringify(schema) },
      { type: 'application/ld+json', children: JSON.stringify(breadcrumbSchema) }
    ]
  })
}


// 2) 是否用「浮動」版型 (image-left / image-right)
const layoutClassFloat = computed(() => {
  return poster.value?.layoutType === 'image-left' 
      || poster.value?.layoutType === 'image-right'
})

// 3) 給圖片套用對應的浮動 class
const imageFloatClass = computed(() => {
  if (poster.value?.layoutType === 'image-left') return 'image-float-left'
  if (poster.value?.layoutType === 'image-right') return 'image-float-right'
  return ''
})

// 4) 否則用 flex 佈局
const layoutClassFlex = computed(() => {
  switch (poster.value?.layoutType) {
    case 'image-left': 
      return 'layout-image-left'
    case 'image-right':
      return 'layout-image-right'
    case 'full-width':
      return 'layout-full-width'
    default:
      return ''
  }
})

// 5) 動畫 class
const textAnimationClass = computed(() => {
  return poster.value?.textAnimation
    ? `text-animation ${poster.value.textAnimation}`
    : 'text-animation'
})
const imageAnimationClass = computed(() => {
  return poster.value?.imageAnimation
    ? `image-animation ${poster.value.imageAnimation}`
    : 'image-animation'
})
</script>

<style scoped>
/* 容器 */
.postersPage-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  line-height: 1.6;
}
.error {
  color: red;
}
.loading {
  text-align: center;
  padding: 40px;
  color: #aaa;
}

/* 搜尋列 */
.search-bar {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.search-input {
  width: 260px;
  max-width: 100%;
  padding: 8px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
}
.search-input:focus {
  border-color: #ff6600;
}
.search-btn {
  padding: 8px 18px;
  background: #ff6600;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;
}
.search-btn:hover {
  background: #e55a00;
}

/* 麵包屑 */
.breadcrumb {
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.breadcrumb a {
  color: #ff6600;
  text-decoration: none;
}
.breadcrumb a:hover {
  text-decoration: underline;
}
.breadcrumb-sep {
  color: #bbb;
  margin: 0 2px;
}
.breadcrumb-current {
  color: #555;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
}

/* ================================
   1) float-container
================================ */
.float-container {
  /* 讓圖片可浮動, 文字繞圖 */
  width: 100%;
}
.float-container::after {
  content: "";
  display: block;
  clear: both; /* 清除浮動, 讓之後區塊不被圖片影響 */
}

/* 左浮動 */
.image-float-left {
  float: left;
  width: 300px;
  margin: 0 20px 20px 0;
}

/* 右浮動 */
.image-float-right {
  float: right;
  width: 300px;
  margin: 0 0 20px 20px;
}

/* 基礎樣式 (通用) */
.image-float-left, .image-float-right {
  height: auto;
  display: block;
  object-fit: cover;
  border-radius: 15px;
  border: 5px solid #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 文字正常 block */
.float-text-content {
  /* 文字會自動繞圖片, 到圖片底部就回到全欄寬 */
}

/* 
  2) 手機版: 圖片放上方, 文字在下方
  將 float 改為 none, width=100%, margin= auto 
*/
@media (max-width: 768px) {
  .image-float-left, .image-float-right {
    float: none;
    width: 100% !important;
    margin: 0 auto 20px;
  }
}

/* ================================
   3) flex 版型
================================ */
.layout-image-left,
.layout-image-right {
  display: flex;
  align-items: flex-start;
}
/* 圖片左 */
.layout-image-left .image-wrapper {
  margin-right: 20px;
  flex: 0 0 40%;
}
/* 圖片右 */
.layout-image-right {
  flex-direction: row-reverse;
}
.layout-image-right .image-wrapper {
  margin-left: 20px;
  flex: 0 0 40%;
}
/* 全幅 */
.layout-full-width {
  display: flex;
  flex-direction: column;
}
.layout-full-width .image-wrapper {
  margin-bottom: 20px;
  width: 100%;
}
.image-wrapper img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  border-radius: 15px;
  border: 5px solid #fff;
  margin-bottom: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* ================================
   4) 動畫
================================ */
.text-animation,
.image-animation {
  opacity: 0;
  transition: opacity 0.8s, transform 0.8s;
}
.fade-left { transform: translateX(-50px); }
.fade-right { transform: translateX(50px); }
.fade-up { transform: translateY(50px); }
.zoom-in { transform: scale(0.8); }
.visible {
  opacity: 1;
  transform: none;
}

/* 返回文章列表按鈕 */
.back-to-list {
  text-align: center;
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}
.btn-back {
  display: inline-block;
  padding: 10px 24px;
  background: #ff6600;
  color: #fff;
  text-decoration: none;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: background 0.2s;
}
.btn-back:hover {
  background: #ff5500;
}
</style>

<style>
/* 文章內產品卡片（v-html 產生，需用非 scoped 樣式） */
.jl-cards { display: flex; gap: 12px; margin: 16px 0 8px; flex-wrap: wrap; }
.jl-card { flex: 1 1 180px; display: flex; gap: 10px; align-items: center; border: 1px solid #eee; border-radius: 12px; padding: 10px; background: #fffaf5; text-decoration: none !important; color: #333 !important; box-shadow: 0 2px 8px rgba(0,0,0,.05); }
.jl-card:hover { border-color: #ffcc99; }
.jl-card img { width: 72px; height: 72px; object-fit: cover; border-radius: 8px; flex: none; }
.jl-step { display: inline-block; font-size: 12px; color: #fff; background: #ff6600; border-radius: 10px; padding: 1px 8px; margin-bottom: 4px; }
.jl-name { font-weight: bold; font-size: 15px; line-height: 1.3; }
.jl-tag { font-size: 12px; color: #888; margin: 2px 0 6px; }
.jl-btn { display: inline-block; font-size: 13px; color: #ff6600; font-weight: bold; }

/* 文章內表格（比較表）：手機可左右滑動 */
.float-text-content table, .text-wrapper table {
  display: block; overflow-x: auto; max-width: 100%;
  border-collapse: collapse; margin: 14px 0; font-size: 0.95em;
}
.float-text-content th, .float-text-content td, .text-wrapper th, .text-wrapper td {
  border: 1px solid #eadfd3; padding: 8px 10px; text-align: left; vertical-align: top;
}
.float-text-content th, .text-wrapper th { background: #fff4ea; white-space: nowrap; }
</style>
